import { apiClient } from './client';

/**
 * Terms of Use / Privacy Policy (LEGAL-TERMS). Versioned documents a
 * platform admin publishes; joining (register, accept invite) sends the ids
 * of the versions the person ticked, and a signed-in user is asked again
 * after a material change. Nothing is required until the first publish.
 */

export type LegalKind = 'terms' | 'privacy';

export const LEGAL_KIND_LABEL: Record<LegalKind, string> = {
  terms: 'Terms of Use',
  privacy: 'Privacy Policy',
};

export const LEGAL_KIND_PATH: Record<LegalKind, string> = {
  terms: '/terms',
  privacy: '/privacy',
};

export interface LegalDocumentSummary {
  id: string;
  kind: LegalKind;
  version: number;
  title: string;
  effective_at: string | null;
}

export interface LegalDocument extends LegalDocumentSummary {
  body_md: string;
  material: boolean;
  published_at: string | null;
}

export interface LegalAdminDocument extends LegalDocument {
  created_at: string;
  acceptance_count: number;
}

export async function getCurrentLegal(): Promise<{
  current: LegalDocumentSummary[];
  upcoming: LegalDocumentSummary[];
}> {
  const { data } = await apiClient.get('/api/v1/legal');
  return data;
}

export async function getLegalKind(kind: LegalKind): Promise<{
  current: LegalDocument | null;
  upcoming: LegalDocument | null;
}> {
  const { data } = await apiClient.get(`/api/v1/legal/${kind}`);
  return data;
}

export async function getLegalVersion(
  kind: LegalKind,
  version: number,
): Promise<LegalDocument> {
  const { data } = await apiClient.get(
    `/api/v1/legal/${kind}/versions/${version}`,
  );
  return data;
}

export async function getLegalStatus(): Promise<{
  pending: LegalDocumentSummary[];
}> {
  const { data } = await apiClient.get('/api/v1/users/me/legal-status');
  return data;
}

export async function acceptLegal(
  documentIds: string[],
): Promise<{ pending: LegalDocumentSummary[] }> {
  const { data } = await apiClient.post('/api/v1/users/me/legal-acceptances', {
    document_ids: documentIds,
  });
  return data;
}

// --- Platform admin ---------------------------------------------------

export async function listLegalDocuments(): Promise<LegalAdminDocument[]> {
  const { data } = await apiClient.get('/api/v1/admin/legal-documents');
  return data;
}

export async function createLegalDraft(body: {
  kind: LegalKind;
  title: string;
  body_md: string;
}): Promise<LegalAdminDocument> {
  const { data } = await apiClient.post('/api/v1/admin/legal-documents', body);
  return data;
}

export async function updateLegalDraft(
  id: string,
  body: { title?: string; body_md?: string },
): Promise<LegalAdminDocument> {
  const { data } = await apiClient.patch(
    `/api/v1/admin/legal-documents/${id}`,
    body,
  );
  return data;
}

export async function deleteLegalDraft(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/admin/legal-documents/${id}`);
}

export async function publishLegalDocument(
  id: string,
  body: { material: boolean; effective_at?: string | null },
): Promise<LegalAdminDocument> {
  const { data } = await apiClient.post(
    `/api/v1/admin/legal-documents/${id}/publish`,
    body,
  );
  return data;
}
